import { useMemo } from "react";
import DataTable from "./DataTable";
import CollapsibleSection from "./CollapsibleSection";
import EmptyState from "./EmptyState";
import { fmtAed } from "./KpiCard";
import { allColumns } from "../lib/columns";
import { buildPairedRows, singleSidedNote, type PairedRow } from "../lib/pairing";
import { normaliseNeedle, rowMatchesTextAt } from "../lib/textSearch";
import MatchEvidenceLegend from "./MatchEvidenceLegend";
import type { EvidenceLegendEntry } from "../lib/matchEvidence";
import type { FrameRecords, SideDisplayConfig } from "../types/api";
import { isDateNarrowed, type RowFilter } from "./StatementTab";

interface Props {
  cri: FrameRecords;
  insurer: FrameRecords;
  filters: RowFilter;
  criConfig?: SideDisplayConfig;
  insConfig?: SideDisplayConfig;
  insurerName?: string;
  // result.match_evidence_legend — the server's Matched By wording. Absent on an
  // older payload, in which case the legend simply does not render.
  evidenceVocabulary?: EvidenceLegendEntry[];
}

// Side-by-Side, paired. The matched groups (Matched / Mismatch) align each CRI
// transaction against the INSURER transaction it was actually matched with, on one
// row — the same match-block rule the Excel paired sheets use (see lib/pairing.ts).
// The unmatched groups (G1/G2) have no counterpart by definition, so they stay
// one-sided lists.
//
// Pure display over columns already in the frames: no reconciliation math, no
// recomputed figures. Rows are only re-ORDERED into pair order.

const MATCHED_GROUPS = [
  { status: "Matched", title: "Matched" },
  { status: "Mismatch", title: "Group 3 — Figure / Balance Mismatch" },
];
const UNMATCHED_GROUPS = [
  { status: "In Insurer, not CRI", title: "Group 1 — In Insurer, not in CRI", side: "ins" as const },
  { status: "In CRI, not Insurer", title: "Group 2 — In CRI, not in Insurer", side: "cri" as const },
];

function isoDay(v: string | number | boolean | null): string {
  const s = String(v ?? "").slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : "";
}

// The display filters, applied identically to both sides (same semantics as
// StatementTab so the two tabs agree on which rows exist).
function filterPositions(frame: FrameRecords, filters: RowFilter, status: string): number[] {
  const c = (n: string) => frame.columns.indexOf(n);
  const cCat = c("recon_category"), cStatus = c("match_status"), cCriType = c("cri_type");
  const cInsType = c("role_legacy"), cLob = c("line_of_business"), cDate = c("doc_date");
  const cPeriod = c("period_position");
  const cCleared = c("cleared_subsequent");
  const needle = normaliseNeedle(filters.text);
  const narrowed = isDateNarrowed(filters);
  const out: number[] = [];
  frame.rows.forEach((r, i) => {
    if (cStatus < 0 || String(r[cStatus] ?? "") !== status) return;
    // A row cleared by a later transaction is not open: it has its own Overview line
    // and its own tab, and the Excel group tabs drop it too (finance, 2026-09-14).
    // Only ever set on a G1/G2 row, so the matched groups are unaffected.
    if (cCleared >= 0 && String(r[cCleared] ?? "").toLowerCase() === "true") return;
    if (filters.categories.length && cCat >= 0 && !filters.categories.includes(String(r[cCat] ?? ""))) return;
    if (filters.statuses.length && !filters.statuses.includes(String(r[cStatus] ?? ""))) return;
    if (filters.criTypes.length && cCriType >= 0 && !filters.criTypes.includes(String(r[cCriType] ?? ""))) return;
    if (filters.insurerTypes.length && cInsType >= 0 && !filters.insurerTypes.includes(String(r[cInsType] ?? ""))) return;
    if (filters.lob.length && cLob >= 0 && !filters.lob.includes(String(r[cLob] ?? ""))) return;
    if (filters.periods.length && cPeriod >= 0
        && !filters.periods.includes(String(r[cPeriod] ?? ""))) return;
    // Undated rows drop only once the range is genuinely narrowed — see StatementTab.
    if ((filters.dateFrom || filters.dateTo) && cDate >= 0) {
      const d = isoDay(r[cDate]);
      if (!d) {
        if (narrowed) return;
      } else {
        if (filters.dateFrom && d < filters.dateFrom) return;
        if (filters.dateTo && d > filters.dateTo) return;
      }
    }
    if (needle && !rowMatchesTextAt(frame, i, needle)) return;
    out.push(i);
  });
  return out;
}

// Does this single-sided row name a partner at all? A matched_row_id pointing at a
// row that isn't in this slice means the counterpart exists but sits elsewhere (the
// other track); no matched_row_id means it is genuinely absent, and the note builder
// then stays silent rather than guessing.
function hasPartner(p: PairedRow, cri: FrameRecords, insurer: FrameRecords): boolean {
  const frame = p.cri !== null ? cri : insurer;
  const pos = p.cri !== null ? p.cri : p.ins;
  if (pos === null) return false;
  const ci = frame.columns.indexOf("matched_row_id");
  if (ci < 0) return false;
  const v = frame.rows[pos][ci];
  return v !== null && v !== undefined && !(typeof v === "number" && Number.isNaN(v));
}

// Sum a side's amount column over the rows actually rendered on that side.
function sumAmounts(frame: FrameRecords, positions: (number | null)[], col: string): number {
  const ci = frame.columns.indexOf(col);
  if (ci < 0) return 0;
  let t = 0;
  for (const p of positions) {
    if (p === null) continue;
    const v = frame.rows[p][ci];
    if (typeof v === "number") t += v;
  }
  return t;
}

export default function SideBySideTab({ cri, insurer, filters, criConfig, insConfig,
                                       insurerName = "", evidenceVocabulary }: Props) {
  const insAmountCol = insurer.columns.includes("balance_amount") ? "balance_amount" : "signed_amount";

  // One combined frame per matched group: each output row is a pair, its cells being
  // the CRI row's cells followed by the insurer row's cells. Feeding DataTable a
  // single frame (rather than two side-by-side grids) is what keeps the two halves
  // locked together — they page, filter, sort and scroll as ONE row.
  const matched = useMemo(() => {
    const criCols = allColumns(cri, criConfig);
    const insCols = allColumns(insurer, insConfig);
    // A trailing note column explains every blank band in place, so a reader never
    // has to guess why one side is empty (mirrors the workbook's in-band note).
    const displayColumns = [
      ...criCols.map((c) => "cri::" + c.key),
      ...insCols.map((c) => "ins::" + c.key),
      "pair_note",
    ];
    // _match_block rides along in the FRAME so the grid can band each match block's rows
    // together, but is kept OUT of the display config: allColumns suppresses underscore
    // names only among the extras it discovers, and renders anything a curated
    // config.columns lists — so listing it there would show a raw "_match_block" column.
    const columns = [...displayColumns, "_match_block"];

    return MATCHED_GROUPS.map(({ status, title }) => {
      const criPos = filterPositions(cri, filters, status);
      const insPos = filterPositions(insurer, filters, status);
      if (!criPos.length && !insPos.length) return null;

      const paired = buildPairedRows(cri, criPos, insurer, insPos);
      const rows: FrameRecords["rows"] = paired.map((p) => {
        const cRow = p.cri !== null ? cri.rows[p.cri] : null;
        const iRow = p.ins !== null ? insurer.rows[p.ins] : null;
        const cells: (string | number | boolean | null)[] = [];
        for (const c of criCols) cells.push(cRow ? cRow[c.index] : null);
        for (const c of insCols) cells.push(iRow ? iRow[c.index] : null);
        // Single-sided row: say WHERE the counterpart is. A partner that exists but
        // isn't in this slice sits on the other track; one in a both-sided block was
        // consumed by another leg of the same many-to-one match.
        const oneSided = (cRow === null) !== (iRow === null);
        cells.push(oneSided ? singleSidedNote(p.bothSided, hasPartner(p, cri, insurer)) : null);
        cells.push(p.blockId);
        return cells;
      });

      // Index by the CRI frame index when present, else the insurer's — keeps a
      // stable per-row key without colliding across the two sides.
      const indices = paired.map((p) =>
        p.cri !== null ? cri.indices[p.cri] : p.ins !== null ? insurer.indices[p.ins] : null);

      const frame: FrameRecords = { columns, rows, indices, total_rows: rows.length };
      const config: SideDisplayConfig = {
        columns: displayColumns,
        // Bare labels: the side is carried by the spanning banner above the header
        // band (DataTable `grouped`), not repeated into every column name.
        renames: Object.fromEntries([
          ...criCols.map((c) => ["cri::" + c.key, c.label]),
          ...insCols.map((c) => ["ins::" + c.key, c.label]),
          ["pair_note", "Note"],
        ]),
      };

      const nPairs = paired.filter((p) => p.cri !== null && p.ins !== null).length;
      const nUnpaired = paired.length - nPairs;

      return {
        title, frame, config, nPairs, nUnpaired,
        criTotal: sumAmounts(cri, paired.map((p) => p.cri), "cri_amount"),
        insTotal: sumAmounts(insurer, paired.map((p) => p.ins), insAmountCol),
      };
    }).filter((s): s is NonNullable<typeof s> => s !== null);
  }, [cri, insurer, filters, criConfig, insConfig, insurerName, insAmountCol]);

  // Stable array identity for the legend: a fresh `matched.map(...)` on every render
  // would defeat the legend's own memo and re-scan every rendered row each time the
  // grid pages, filters or moves the selection.
  const matchedFrames = useMemo(() => matched.map((s) => s.frame), [matched]);

  // G1/G2 have no counterpart by definition — one-sided lists, unchanged.
  const unmatched = useMemo(() => {
    return UNMATCHED_GROUPS.map(({ status, title, side }) => {
      const frameSrc = side === "cri" ? cri : insurer;
      const pos = filterPositions(frameSrc, filters, status);
      if (!pos.length) return null;
      const frame: FrameRecords = {
        columns: frameSrc.columns,
        rows: pos.map((p) => frameSrc.rows[p]),
        indices: pos.map((p) => frameSrc.indices[p]),
        total_rows: pos.length,
      };
      const amountCol = side === "cri" ? "cri_amount" : insAmountCol;
      return {
        title, frame, side,
        config: side === "cri" ? criConfig : insConfig,
        subtotal: sumAmounts(frameSrc, pos, amountCol),
        count: pos.length,
      };
    }).filter((s): s is NonNullable<typeof s> => s !== null);
  }, [cri, insurer, filters, criConfig, insConfig, insAmountCol]);

  if (!matched.length && !unmatched.length) {
    return <EmptyState title="No items match the current filters"
      body="Every pair in this tab is filtered out. Clear the date range or search box above to see them again." />;
  }

  return (
    <div>
      <p className="muted" style={{ margin: "0 0 10px" }}>
        Matched rows are <strong>paired</strong>: each row shows the CRI transaction on the left and
        the {insurerName || "insurer"} transaction it was matched with on the right — the same pairing
        as the Matched / G3 sheets in the Annotated Workbook. A blank band means the counterpart is
        elsewhere; the note column says where. An <strong>aggregated</strong> match — several rows
        settling one — is shaded as one block and named in the <strong>Match Group</strong> column
        (e.g. <code>3:1 (voucher)</code>); an ordinary pair reads <code>1:1</code>.
      </p>

      {/* The key to the Matched By chips, counted over the pairs actually rendered
          below. Mirrors the legend band at the top of the workbook's paired sheets. */}
      <MatchEvidenceLegend frames={matchedFrames} vocabulary={evidenceVocabulary} />

      {matched.map((sec) => (
        <CollapsibleSection
          key={sec.title}
          title={sec.title}
          sub={`CRI ${fmtAed(sec.criTotal)}  |  ${insurerName || "Insurer"} ${fmtAed(sec.insTotal)}   (${sec.nPairs.toLocaleString()} paired${
            sec.nUnpaired ? `, ${sec.nUnpaired.toLocaleString()} unpaired` : ""})`}
          defaultOpen={sec.title === "Matched"}
        >
          <DataTable frame={sec.frame} pageSize={50} config={sec.config}
            pinSide="paired" insurerName={insurerName} grouped title={sec.title}
            groupKeyCol="_match_block" />
        </CollapsibleSection>
      ))}

      {unmatched.map((sec) => (
        <CollapsibleSection
          key={sec.title}
          title={sec.title}
          sub={`${fmtAed(sec.subtotal)}   (${sec.count.toLocaleString()} items)`}
          defaultOpen={false}
        >
          <DataTable frame={sec.frame} pageSize={50} config={sec.config}
            pinSide={sec.side === "cri" ? "cri" : "insurer"} insurerName={insurerName} title={sec.title}
            keyColumnsFirst />
        </CollapsibleSection>
      ))}
    </div>
  );
}
